import i18n from 'i18n-js';

import { addTimeZoneToLocalTime, localTime } from '@helpers';

const getDataStorageFormat = (period) => {
  if (period === 'hour') return 'MM-DD HH:00';
  if (period === 'isoWeek') return 'GGGG-WW';
  return 'MM-DD';
};

const buildIsoWeekLabels = (start) => {
  const week = addTimeZoneToLocalTime(start || '').startOf('isoWeek');
  const labels = [];
  for (let i = 0; i < 8; i++) {
    labels.push(`W${week.isoWeek()}`);
    week.add(1, 'isoWeek');
  }
  return labels;
};

export const getValuesLine = ({ start: startStr, data, amount, period }) => {
  const dataStorage = {};
  const isHour = period == 'hour';
  const format = getDataStorageFormat(period);

  const dataLineActive = [];
  const dataLineProductive = [];

  if (isHour) {
    dataLineActive.push(0);
    dataLineProductive.push(0);
  }

  data.forEach((item) => {
    const { totalActiveSecs, totalProductiveSecs, dateTime, date } = item;
    // Backend sends datetime in factory tz with no indicator, so parse as-is. Must match the
    // addTimeZoneToLocalTime lookup below — localTime forces UTC and shifts hourly buckets out of the day window, dropping active time (BUG 5265)
    const time = addTimeZoneToLocalTime(dateTime || date);
    let day = time.format(format);
    const active = totalActiveSecs;
    const productive = totalProductiveSecs;

    dataStorage[day] = {
      active,
      productive,
    };
  });
  let activeAcc = 0;
  let productiveAcc = 0;
  let startCounting = addTimeZoneToLocalTime(startStr);
  for (let i = 1; i <= amount; i++) {
    const value = dataStorage[startCounting.format(format)];
    activeAcc += value?.active || 0;
    productiveAcc += value?.productive || 0;
    dataLineActive.push(activeAcc);
    dataLineProductive.push(productiveAcc);
    startCounting.add(1, period);
  }

  return {
    totalActive: activeAcc,
    totalProductive: productiveAcc,
    dataLineActive,
    dataLineProductive,
  };
};

export const getValuesBar = ({ start: startStr, data, amount, period }) => {
  const dataStorage = {};
  const isIsoWeek = period == 'isoWeek';
  const format = getDataStorageFormat(period);
  const dataBar = [];

  const baseLineObject = {
    active: 0,
    productive: 0,
    setup: 0,
    change: 0,
  };

  let totalActive = 0;
  let totalChange = 0;
  let totalSetup = 0;
  let totalProductive = 0;

  data.forEach((item) => {
    const { totalActiveSecs, totalProductiveSecs, totalChangeSecs, totalSetupSecs, dateTime, date } = item;
    // Backend sends datetime in factory tz with no indicator, so parse as-is. Must match the
    // addTimeZoneToLocalTime lookup below — localTime forces UTC and shifts hourly buckets out of the day window, dropping active time (BUG 5265)
    const time = addTimeZoneToLocalTime(dateTime || date);
    let day = time.format(format);
    const active = totalActiveSecs;
    const change = totalChangeSecs;
    const setup = totalSetupSecs;
    const productive = totalProductiveSecs;

    dataStorage[day] = {
      ...item,
      active,
      productive,
      setup,
      change,
    };
  });

  let startCounting = addTimeZoneToLocalTime(startStr);
  for (let i = 1; i <= amount; i++) {
    const value = dataStorage[startCounting.format(format)] || baseLineObject;

    let storage = {
      productive: 0,
      active: 0,
      change: 0,
      setup: 0,
    };
    const checkNumber = (n) => (n < 10 ? `0${Number(n)}` : Number(n));

    storage.productive += value.productive;
    storage.active += value.active;
    storage.change += value.change;
    storage.setup += value.setup;
    storage.date = isIsoWeek
      ? `${i18n.t('Week')} ${startCounting.isoWeek()} ${startCounting.isoWeekYear()}`
      : `${startCounting.format('DD')} ${i18n.t(startCounting.format('MMM'))} ${startCounting.format('YYYY')} ${checkNumber(
          startCounting.format('HH')
        )}h-${checkNumber(Number(startCounting.format('HH')) + 1)}h`;
    storage.dateISO = startCounting.format();

    totalActive += value.active;
    totalChange += value.change;
    totalSetup += value.setup;
    totalProductive += value.productive;

    dataBar.push({
      ...storage,
    });

    startCounting.add(1, period);
  }

  return {
    dataBar,
    totalActive,
    totalProductive,
    totalChange,
    totalSetup,
  };
};

const formatNumber = (n) => (n < 10 ? `0${n}` : n);

export const getBarLegend = (selectedTime, start) => {
  switch (selectedTime) {
    case 'day': {
      const legend = [];
      Array.from({ length: 24 }, (_, el) => {
        legend.push(`${formatNumber(el)}`);
      });
      return { legend, legendRules: ['00', '06', '12', '18'] };
    }
    case 'week': {
      const legend = [i18n.t('Mon'), i18n.t('Tue'), i18n.t('Wed'), i18n.t('Thu'), i18n.t('Fri'), i18n.t('Sat'), i18n.t('Sun')];
      return { legend, legendRules: legend };
    }
    case 'month': {
      const startMonth = addTimeZoneToLocalTime(start || '').startOf('month');
      const legend = Array.from({ length: startMonth.daysInMonth() }, (_, i) => i + 1);
      return { legend, legendRules: [7, 14, 21, 28] };
    }
    case 'year': {
      const startYear = addTimeZoneToLocalTime(start).startOf('year');
      const legend = [];

      Array.from({ length: 12 }, (_, el) => {
        legend.push(startYear.format('MMM').charAt(0));
        startYear.add(1, 'month');
      });
      return { legend, legendRules: legend };
    }
    case 'eightWeeks': {
      const legend = buildIsoWeekLabels(start);
      return { legend, legendRules: legend };
    }
    default:
      return { legend: [], legendRules: [] };
  }
};

export const getLineLegend = ({ selectedTime, start, end, showFullInfo }) => {
  const { legendItems, colorDots, currentLegendInit, colorWhiteDots } = (() => {
    switch (selectedTime) {
      case 'day': {
        const colorWhiteDots = [];
        const colorDots = ['00:00', '06:00', '12:00', '18:00', '23:59'];

        const legendItems = Array.from({ length: 24 }, (_, el) => {
          const d = `${formatNumber(el)}:00`;
          if (!colorDots.includes(d)) {
            colorWhiteDots.push(d);
          }
          return d;
        });
        legendItems.push(`23:59`);
        return {
          legendItems,
          currentLegendInit: localTime().format('HH:00'),
          colorDots,
          colorWhiteDots,
        };
      }
      case 'week': {
        const legendItems = [];
        const startWeek = addTimeZoneToLocalTime(start);

        const colorDots = [];
        const colorWhiteDots = [];
        const colorWhiteDotsEl = [6, 12, 18];
        Array.from({ length: 7 }, (_, index) => {
          const i = startWeek.format('ddd');
          startWeek.add(1, 'day');
          Array.from({ length: 24 }, (_, el) => {
            const d = `${i} ${formatNumber(el)}:00`;
            if (el === 0) {
              colorDots.push(d);
            }
            if (colorWhiteDotsEl.includes(el)) {
              colorWhiteDots.push(d);
            }
            legendItems.push(d);
          });
          if (index === 6) {
            const d = `${i} 23:59`;
            colorDots.push(d);
            legendItems.push(d);
          }
        });
        return {
          colorDots: colorDots,
          colorWhiteDots: colorWhiteDots,
          currentLegendInit: `${localTime().format('ddd')} ${localTime().format('HH:00')}`,
          legendItems,
        };
      }
      case 'month': {
        const legendItems = [];
        const amountStart = addTimeZoneToLocalTime(start).daysInMonth();
        const amountEnd = addTimeZoneToLocalTime(end).daysInMonth();

        const date = amountStart > amountEnd ? start : end;
        const startMonth = addTimeZoneToLocalTime(date).startOf('month');
        const amount = addTimeZoneToLocalTime(date).daysInMonth();

        for (let i = 1; i <= amount; i++) {
          legendItems.push(`${i}`);
          startMonth.add(1, 'day');
        }

        const colorWhiteDots = [];
        const colorDots = [legendItems[0], legendItems[legendItems.length - 1]];
        const colorDotsEl = [6, 11, 16, 21, 26];
        const skipItems = [1, 31];

        Array.from({ length: 31 }, (_, i) => {
          const el = i + 1;
          if (skipItems.includes(el)) {
            return;
          }

          if (colorDotsEl.includes(el)) {
            colorDots.push(`${el}`);
          } else {
            colorWhiteDots.push(`${el}`);
          }
        });

        return {
          currentLegendInit: `${Number(localTime().format('DD'))}`,
          legendItems,
          colorDots,
          colorWhiteDots,
        };
      }
      case 'year': {
        const startYear = addTimeZoneToLocalTime(start).startOf('year');
        const legendItems = [];
        const colorDots = [];
        const colorWhiteDots = [];
        const yearAmount = addTimeZoneToLocalTime(end).endOf('year').dayOfYear();
        const current = `${localTime().format('ddd')} ${localTime().format('DD')} ${localTime().format('MMM')}`;

        const monthHighlighted = ['3', '6', '9'];
        for (let i = 1; i < yearAmount; i++) {
          if (localTime().format('DD-MM') == startYear.format('DD-MM')) {
            legendItems.push(current);
            startYear.add(1, 'day');
            continue;
          }

          if (i === 1 || i === yearAmount - 1) {
            legendItems.push(`${startYear.format('MMM')}`);
            colorDots.push(`${startYear.format('MMM')}`);
          } else {
            const current = `${startYear.format('ddd')} ${startYear.format('MMM')} ${Number(startYear.format('DD'))}`;
            if (Number(startYear.format('DD')) === 1) {
              if (monthHighlighted.includes(startYear.format('M'))) {
                colorDots.push(current);
              } else {
                colorWhiteDots.push(current);
              }
            }
            legendItems.push(current);
          }
          startYear.add(1, 'day');
        }

        return {
          colorDots,
          legendItems,
          currentLegendInit: current,
          colorWhiteDots,
        };
      }
      case 'eightWeeks': {
        const legendItems = buildIsoWeekLabels(start);
        const currentIsoLabel = `W${localTime().isoWeek()}`;
        const currentLegendInit = legendItems.includes(currentIsoLabel) ? currentIsoLabel : legendItems[legendItems.length - 1];
        return { legendItems, colorDots: [...legendItems], colorWhiteDots: [], currentLegendInit };
      }
      default:
        return {
          legendItems: [],
          colorDots: [],
          currentLegendInit: '',
        };
    }
  })();

  const currentLegend = showFullInfo ? legendItems[legendItems.length - 1] : currentLegendInit;

  colorDots.push(currentLegend);

  const bigLegendStorage = {
    day: ['00:00', currentLegend, '23:59'],
    week: [legendItems[0], currentLegend, legendItems[legendItems.length - 1]],
    month: [legendItems[0], currentLegend, legendItems[legendItems.length - 1]],
    year: [legendItems[0], currentLegend, legendItems[legendItems.length - 1]],
    eightWeeks: [legendItems[0], currentLegend, legendItems[legendItems.length - 1]],
  };

  const legendOffsetStorage = {
    day: 4,
    week: 48,
    month: 5,
    year: 60,
    eightWeeks: 12,
  };
  const legendOffset = legendOffsetStorage[selectedTime];

  const bigLegend = bigLegendStorage[selectedTime];

  const displayIndex = showFullInfo ? legendItems.length - 1 : legendItems.indexOf(currentLegend);

  return { legendOffset, bigLegend, colorDots, displayIndex, legendItems, currentLegend, colorWhiteDots };
};
